// Client for the per-user Insights memory endpoint — lets a user view/edit the
// durable notes the Insights agent keeps about them (and lets FleetOps staff
// inspect another user's, via targetUserId). Posts through the API gateway via
// the CloudFunctionClientContext `api` instance — never a direct Firebase call.
// The gateway sets x-account-id from the apisauce instance; identity is the
// verified bearer token. Server contract:
// functions/src/insights/memory/endpoints.ts.

import { ApisauceInstance } from 'apisauce';

export const INSIGHTS_MEMORY_PATH = '/v1/content/insightsMemory';

// The agent's virtual memory root — every memory path lives under it. Mirrors
// MEMORY_ROOT in functions/src/insights/memory/constants.ts (server contract).
export const MEMORY_ROOT = '/memories';

export interface InsightsMemoryEntry {
  // In the agent's virtual `/memories` path space, e.g. `/memories/prefs.md`.
  path: string;
  sizeBytes: number;
}

// Only staff may pass targetUserId (the server 403s otherwise); everyone else
// operates on their own memory, keyed off the verified token server-side.
type ScopeOpts = { targetUserId?: string };

const withTarget = (opts: ScopeOpts): Record<string, string> =>
  opts.targetUserId ? { targetUserId: opts.targetUserId } : {};

// Returns null on request failure so the caller can distinguish an error from a
// user who genuinely has no memory yet (an empty array).
export const listInsightsMemory = async (
  api: ApisauceInstance,
  opts: ScopeOpts = {},
): Promise<InsightsMemoryEntry[] | null> => {
  const response = await api.post<{ entries?: InsightsMemoryEntry[] }>(
    INSIGHTS_MEMORY_PATH,
    { action: 'list', ...withTarget(opts) },
  );
  if (!response.ok || !response.data) {
    return null;
  }
  return response.data.entries ?? [];
};

// Returns the file's content, or null if it is missing / the request failed.
export const readInsightsMemory = async (
  api: ApisauceInstance,
  { path, ...opts }: ScopeOpts & { path: string },
): Promise<string | null> => {
  const response = await api.post<{ content?: string }>(INSIGHTS_MEMORY_PATH, {
    action: 'read',
    path,
    ...withTarget(opts),
  });
  if (!response.ok || !response.data) {
    return null;
  }
  return response.data.content ?? '';
};

// Creates or overwrites a file. Returns whether the write succeeded.
export const writeInsightsMemory = async (
  api: ApisauceInstance,
  { path, content, ...opts }: ScopeOpts & { path: string; content: string },
): Promise<boolean> => {
  const response = await api.post(INSIGHTS_MEMORY_PATH, {
    action: 'write',
    path,
    content,
    ...withTarget(opts),
  });
  return response.ok === true;
};

export const deleteInsightsMemory = async (
  api: ApisauceInstance,
  { path, ...opts }: ScopeOpts & { path: string },
): Promise<boolean> => {
  const response = await api.post(INSIGHTS_MEMORY_PATH, {
    action: 'delete',
    path,
    ...withTarget(opts),
  });
  return response.ok === true;
};
